import { CalendarDaysIcon } from "@heroicons/react/20/solid";
import moment from "moment";
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import Datepicker from "react-tailwindcss-datepicker";

const RangePicker = ({ test, setRangePicker, width }) => {
  // Initializing dates
  const today = moment().startOf("day");
  const todayStartDate = moment.utc().startOf("day");
  const todayEndDate = moment.utc().endOf("day");

  // State to hold the displayed date range
  const [dateToShow, setDateToShow] = useState({
    startDate: today.toDate(),
    endDate: today.toDate(),
  });

  const handleValueChange = (newValue) => {
    const startDate = newValue.startDate ? moment.utc(newValue.startDate).startOf("day") : todayStartDate;
    const endDate = newValue.endDate ? moment.utc(newValue.endDate).endOf("day") : startDate;
    const startDateShow = newValue.startDate ? moment(newValue.startDate).startOf("day") : today;
    const endDateShow = newValue.endDate ? moment(newValue.endDate).endOf("day") : startDateShow;

    const startDateUTCUnix = startDate.unix();
    const endDateUTCUnix = endDate.unix();

    setRangePicker({ startDate: startDateUTCUnix, endDate: endDateUTCUnix });

    setDateToShow({ startDate: startDateShow.toDate(), endDate: endDateShow.toDate() });
  };

  // Initial setup
  useEffect(() => {
    setRangePicker({
      startDate: todayStartDate.unix(),
      endDate: todayEndDate.unix(),
    });
  }, []);

  // UK date format ("Day Date Month", e.g. "Mon 02 June").
  // The year is shown only when the selected range falls outside the current year.
  const currentYear = moment().year();
  const isCurrentYearRange =
    moment(dateToShow.startDate).year() === currentYear && moment(dateToShow.endDate).year() === currentYear;
  const displayFormat = isCurrentYearRange ? "ddd DD MMMM" : "ddd DD MMMM YYYY";

  // Keep the default shortcuts and add "This Year" and "Last Year".
  const shortcutsConfig = {
    today: "Today",
    yesterday: "Yesterday",
    past: (period) => `Last ${period} days`,
    currentMonth: "This month",
    pastMonth: "Last month",
    thisYear: {
      text: "This Year",
      period: {
        start: moment().startOf("year").toDate(),
        end: moment().toDate(),
      },
    },
    lastYear: {
      text: "Last Year",
      period: {
        start: moment().subtract(1, "year").startOf("year").toDate(),
        end: moment().subtract(1, "year").endOf("year").toDate(),
      },
    },
  };

  // The text the picker renders in its input (matches the library: "start ~ end").
  const displayText = `${moment(dateToShow.startDate).format(displayFormat)} ~ ${moment(dateToShow.endDate).format(displayFormat)}`;

  // Size the picker to fit its text so there is no empty gap before the calendar icon.
  // We measure the rendered text width and add the input padding (pl-4 + pr-8) plus the icon.
  const measureRef = useRef(null);
  const [pickerWidth, setPickerWidth] = useState(undefined);

  useLayoutEffect(() => {
    if (measureRef.current) {
      setPickerWidth(Math.ceil(measureRef.current.offsetWidth) + 56);
    }
  }, [displayText]);

  return (
    <div className={`relative ${width || ""}`} style={pickerWidth ? { width: pickerWidth } : undefined}>
      {/* Hidden mirror of the input text, used only to measure its rendered width */}
      <span ref={measureRef} className="invisible absolute whitespace-nowrap text-sm font-light tracking-wide" aria-hidden="true">
        {displayText}
      </span>
      <Datepicker
        value={dateToShow}
        onChange={handleValueChange}
        showShortcuts
        showFooter
        readOnly
        popoverDirection="down"
        toggleClassName="hidden"
        configs={{ shortcuts: shortcutsConfig }}
        inputClassName="w-full pr-8 border-gray-400 rounded-full focus:ring-0 text-sm placeholder:text-gray-400"
        placeholder="Select Dates"
        displayFormat={displayFormat}
      />
      <span className="absolute right-3 top-2 z-0">
        <CalendarDaysIcon className="h-5 w-5 text-primary-color-300" />
      </span>
    </div>
  );
};

export default RangePicker;
